const CONNECTIVITY_META = {
  online: { label: "連線正常", priority: 5 },
  delayed: { label: "連線延遲", priority: 2 },
  offline: { label: "離線", priority: 1 },
  never_connected: { label: "從未連線", priority: 3 },
  unavailable: { label: "無法判定", priority: 4 },
};

const OPEN_INCIDENT_FIELDS = [
  "id",
  "opened_at",
  "notification_state",
  "offline_attempt_count",
  "offline_next_attempt_at",
  "offline_notified_at",
  "offline_last_error_code",
  "offline_terminal_at",
  "offline_suppressed_at",
  "resolved_at",
  "recovery_attempt_count",
  "recovery_next_attempt_at",
  "recovery_notified_at",
  "recovery_last_error_code",
  "recovery_terminal_at",
  "recovery_suppressed_at",
];

const NOTIFICATION_EVIDENCE_FIELDS = [
  "incident_id",
  "notification_type",
  "status",
  "created_at",
];

const DEVICE_FIELDS = [
  "id",
  "device_name",
  "lifecycle_state",
  "venue_id",
  "zone_id",
  "token_status",
  "last_sync_at",
  "last_sync_status",
  "last_health_at",
  "app_version",
  "updated_at",
];

const SNAPSHOT_FIELDS = [
  "last_sync_at",
  "last_sync_status",
  "last_health_at",
  "app_version",
];

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function pick(source, fields) {
  if (!isRecord(source)) return {};
  return fields.reduce((result, field) => {
    if (Object.prototype.hasOwnProperty.call(source, field)) result[field] = source[field];
    return result;
  }, {});
}

function normalizedNullableRecord(source, fields, identityField) {
  if (!isRecord(source) || source[identityField] === null || source[identityField] === undefined) {
    return null;
  }
  return pick(source, fields);
}

export function connectivityMeta(state) {
  return CONNECTIVITY_META[state] || CONNECTIVITY_META.unavailable;
}

export function normalizeConnectivity(connectivity) {
  if (!isRecord(connectivity) || !Object.prototype.hasOwnProperty.call(CONNECTIVITY_META, connectivity.state)) {
    return {
      state: "unavailable",
      label: CONNECTIVITY_META.unavailable.label,
      last_reported_at: null,
      elapsed_ms: null,
      source: null,
    };
  }

  const elapsed = connectivity.elapsed_ms;
  return {
    state: connectivity.state,
    label: typeof connectivity.label === "string" && connectivity.label.trim()
      ? connectivity.label.trim()
      : connectivityMeta(connectivity.state).label,
    last_reported_at: connectivity.last_reported_at ?? null,
    elapsed_ms: typeof elapsed === "number" && Number.isFinite(elapsed) && elapsed >= 0
      ? elapsed
      : null,
    source: typeof connectivity.source === "string" && connectivity.source.trim()
      ? connectivity.source.trim()
      : null,
  };
}

export function formatElapsedMs(elapsedMs) {
  if (typeof elapsedMs !== "number" || !Number.isFinite(elapsedMs) || elapsedMs < 0) return "—";
  const elapsed = elapsedMs;

  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 1) return "不到 1 分鐘";
  if (minutes < 60) return `約 ${minutes} 分鐘`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `約 ${hours} 小時`;
  return `約 ${Math.floor(hours / 24)} 天`;
}

export function normalizeOpenIncident(openIncident) {
  return normalizedNullableRecord(openIncident, OPEN_INCIDENT_FIELDS, "id");
}

function normalizeNotificationPhase(evidence) {
  return normalizedNullableRecord(evidence, NOTIFICATION_EVIDENCE_FIELDS, "incident_id");
}

export function normalizeNotificationEvidence(notificationEvidence) {
  return {
    offline: normalizeNotificationPhase(notificationEvidence?.offline),
    recovery: normalizeNotificationPhase(notificationEvidence?.recovery),
  };
}

export function normalizeObservabilityLimits(observabilityLimits) {
  return {
    display_power: observabilityLimits?.display_power === "unknown"
      ? "unknown"
      : "unknown",
    local_cached_playback: observabilityLimits?.local_cached_playback === "unknown"
      ? "unknown"
      : "unknown",
  };
}

function normalizeLatestReportedSnapshot(snapshot) {
  const normalized = pick(snapshot, SNAPSHOT_FIELDS);
  return SNAPSHOT_FIELDS.reduce((result, field) => {
    result[field] = normalized[field] ?? null;
    return result;
  }, {});
}

function normalizeAlerts(alerts) {
  return (Array.isArray(alerts) ? alerts : []).map((alert) => pick(alert, [
    "alert_type",
    "severity",
    "created_at",
  ]));
}

export function buildDeviceViewModel(device, context = {}) {
  const source = isRecord(device) ? device : {};
  const normalized = pick(source, DEVICE_FIELDS);
  return {
    ...normalized,
    alerts: normalizeAlerts(source.alerts),
    connectivity: normalizeConnectivity(source.connectivity),
    open_incident: normalizeOpenIncident(source.open_incident),
    notification_evidence: normalizeNotificationEvidence(source.notification_evidence),
    latest_reported_snapshot: normalizeLatestReportedSnapshot(source.latest_reported_snapshot),
    observability_limits: normalizeObservabilityLimits(source.observability_limits),
    is_unbound: context.isUnbound === true,
  };
}

export function lifecycleActionAvailability(device, { isUnbound = false, validVenueCount = 0 } = {}) {
  const active = String(device?.lifecycle_state || "").toUpperCase() === "ACTIVE";
  return {
    detail: true,
    repair: active && !isUnbound && Boolean(device?.venue_id),
    transfer: active && validVenueCount > 0,
    replace: active,
    retire: active,
  };
}

export function isHistoricalLifecycle(state) {
  const normalized = String(state || "").toUpperCase();
  return normalized === "RETIRED" || normalized === "REVOKED";
}

export function canonicalReportedAtMs(value) {
  if (typeof value !== "string" && typeof value !== "number") return null;
  if (typeof value === "string" && !value.trim()) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

const HISTORICAL_PRIORITY = 6;

export function devicePresentationPriority(device) {
  if (isHistoricalLifecycle(device?.lifecycle_state)) return HISTORICAL_PRIORITY;
  if (device?.open_incident) return 0;
  return connectivityMeta(device?.connectivity?.state).priority;
}

export function compareDeviceViewModels(left, right) {
  const priorityDiff = devicePresentationPriority(left) - devicePresentationPriority(right);
  if (priorityDiff !== 0) return priorityDiff;

  const leftReportedAt = canonicalReportedAtMs(left?.connectivity?.last_reported_at);
  const rightReportedAt = canonicalReportedAtMs(right?.connectivity?.last_reported_at);
  if (leftReportedAt === null && rightReportedAt !== null) return 1;
  if (leftReportedAt !== null && rightReportedAt === null) return -1;
  if (leftReportedAt !== null && rightReportedAt !== null && leftReportedAt !== rightReportedAt) {
    return rightReportedAt - leftReportedAt;
  }

  const nameDiff = String(left?.device_name || "").localeCompare(
    String(right?.device_name || ""),
    "zh-TW",
  );
  if (nameDiff !== 0) return nameDiff;

  const leftId = String(left?.id || "");
  const rightId = String(right?.id || "");
  if (leftId < rightId) return -1;
  if (leftId > rightId) return 1;
  return 0;
}

export function flattenFleetDevices(fleet) {
  const venueDevices = [];
  const venues = Array.isArray(fleet?.venues) ? fleet.venues : [];

  venues.forEach((venue) => {
    const zones = Array.isArray(venue?.zones) ? venue.zones : [];
    zones.forEach((zone) => {
      (Array.isArray(zone?.devices) ? zone.devices : []).forEach((device) => {
        venueDevices.push(buildDeviceViewModel(device, { isUnbound: false }));
      });
    });
    (Array.isArray(venue?.unzoned_devices) ? venue.unzoned_devices : []).forEach((device) => {
      venueDevices.push(buildDeviceViewModel(device, { isUnbound: false }));
    });
  });

  const unboundDevices = (Array.isArray(fleet?.unbound_devices) ? fleet.unbound_devices : [])
    .map((device) => buildDeviceViewModel(device, { isUnbound: true }));

  return { venueDevices, unboundDevices };
}

function hasDeduplicableDeviceId(device) {
  if (device?.id === null || device?.id === undefined) return false;
  return String(device.id).trim() !== "";
}

export function buildFleetPresentation(fleet) {
  const source = isRecord(fleet) ? fleet : {};
  const seen = new Set();
  const keepFirstDeviceId = (device) => {
    if (!hasDeduplicableDeviceId(device)) return true;
    const key = String(device.id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  };
  const venues = (Array.isArray(source.venues) ? source.venues : []).map((venue) => ({
    ...venue,
    zones: (Array.isArray(venue?.zones) ? venue.zones : []).map((zone) => ({
      ...zone,
      devices: (Array.isArray(zone?.devices) ? zone.devices : []).filter(keepFirstDeviceId),
    })),
  }));

  venues.forEach((venue) => {
    venue.unzoned_devices = (Array.isArray(venue?.unzoned_devices) ? venue.unzoned_devices : [])
      .filter(keepFirstDeviceId);
  });

  return {
    ...source,
    venues,
    unbound_devices: (Array.isArray(source.unbound_devices) ? source.unbound_devices : [])
      .filter(keepFirstDeviceId),
  };
}

export function buildFleetInventory(fleet) {
  const presentation = buildFleetPresentation(fleet);
  const { venueDevices, unboundDevices } = flattenFleetDevices(presentation);
  const uniqueDevices = [...venueDevices, ...unboundDevices];
  const activeDevices = uniqueDevices.filter(
    (device) => String(device?.lifecycle_state || "").toUpperCase() === "ACTIVE",
  );
  const historicalDevices = uniqueDevices.filter(
    (device) => isHistoricalLifecycle(device?.lifecycle_state),
  );
  return {
    activeDevices,
    historicalDevices,
    counts: {
      active: activeDevices.length,
      historical: historicalDevices.length,
      total: uniqueDevices.length,
    },
  };
}

export function buildOperationalSummary(activeDevices) {
  const devices = Array.isArray(activeDevices) ? activeDevices : [];
  if (devices.length === 0) {
    return { kind: "empty", message: "目前沒有啟用中的設備" };
  }

  const attentionCount = devices.filter(
    (device) => device?.open_incident || device?.connectivity?.state !== "online",
  ).length;
  if (attentionCount > 0) {
    return {
      kind: "attention",
      message: `${devices.length} 台啟用中，${attentionCount} 台需要注意`,
    };
  }

  const elapsedValues = devices
    .map((device) => device?.connectivity?.elapsed_ms)
    .filter((value) => typeof value === "number" && Number.isFinite(value) && value >= 0);
  const latestElapsedMs = elapsedValues.length > 0 ? Math.min(...elapsedValues) : null;
  const reportClause = latestElapsedMs === null
    ? ""
    : devices.length === 1
      ? `，最後回報${formatElapsedMs(latestElapsedMs)}前`
      : `，最近一次回報${formatElapsedMs(latestElapsedMs)}前`;
  return {
    kind: "healthy",
    message: `${devices.length} 台設備連線正常${reportClause}`,
  };
}

export function fleetHasContent(fleet) {
  return (Array.isArray(fleet?.venues) && fleet.venues.length > 0)
    || (Array.isArray(fleet?.unbound_devices) && fleet.unbound_devices.length > 0);
}
